import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supplierAuthService } from "../../services/auth.service";
import { AuthResponse, AuthContextType } from "../../types/auth";

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AuthResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const storedUser = supplierAuthService.getUserData();
        if (storedUser) {
            setUser(storedUser);
        }
        setIsLoading(false);
    }, []);

    const login = async (credentials: { username: string; password: string }) => {
        const response = await supplierAuthService.login(credentials);
        setUser(response);
    };

    const logout = () => {
        supplierAuthService.logout();
        setUser(null);
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: !!user, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};

/**
 * Same as useAuth but guarantees user is not null.
 * Use inside any authenticated route – no more null checks.
 */
export const useRequiredAuth = () => {
    const auth = useAuth();
    if (!auth.user) throw new Error("useRequiredAuth used without a logged-in user");
    return { ...auth, user: auth.user, userId: parseInt(auth.user.userId, 10), supplierId: parseInt(auth.user.supplierId, 10) };
};

